import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAssociatedTalentsInit, addBatchMatches } from "../../redux/actions";
import "./QueryPage.css";
import QueryPageHeader from "./components/organisms/QueryPageHeader";
import FilterDrawer from "./components/organisms/FilterDrawer";
import TalentSection from "./components/organisms/TalentSection";
import Pagination from "./components/organisms/Pagination";
import PlaceBidModal from "./components/organisms/PlaceBidModal";
import SelectJobModal from "./components/organisms/SelectJobModal";
import InternalTalentCard from "./components/organisms/InternalTalentCard";
import QueryEmptyState from "./components/organisms/QueryEmptyState";
import Loader from "../../components/Loading/Loader";
import { InternalTalentIcon } from "./QueryPageIcons";
import {
  INITIAL,
  getActiveFilterCount,
} from "./components/organisms/FilterDrawerPanels/filterOptions";
import { buildInternalFiltersPayload } from "./filterPayloadMappers";
import {
  buildQueryResultSearch,
  parseQueryResultState,
} from "./queryStateUrl";
import { canUseSubscriptionFeatures } from "../../shared/billing";

const PAGE_SIZE = 20;

const InternalTalentResultsPage = () => {
  const { searchValue } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures();

  const [selectedTalentIds, setSelectedTalentIds] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(INITIAL);
  const [matchesOpen, setMatchesOpen] = useState(false);
  const [bidModal, setBidModal] = useState({
    open: false,
    talent: null,
    talentType: null,
    skillListingId: null,
    skillListingIds: null,
  });

  const searchTerm = searchValue ? decodeURIComponent(searchValue) : "";
  const [queryState, setQueryState] = useState(() =>
    parseQueryResultState(location.search)
  );
  const { filters, sortOrder, currentPage } = queryState;
  const isAiMode = useSelector((state) => state.nlSearchExternalReducer.isActive);
  const {
    data: talents,
    loading,
    totalCount: rawTotalCount,
  } = useSelector((state) => state.associatedTalentsReducer);
  const safeTalents = Array.isArray(talents) ? talents : [];

  const updateQueryState = (nextState = {}) => {
    const nextSearch = buildQueryResultSearch({
      filters: nextState.filters ?? filters,
      sortOrder: nextState.sortOrder ?? sortOrder,
      currentPage: nextState.currentPage ?? currentPage,
    });
    const nextQueryState = parseQueryResultState(nextSearch);

    if (nextSearch !== location.search) {
      history.push({
        pathname: location.pathname,
        search: nextSearch,
      });
    }

    setQueryState(nextQueryState);
    return nextQueryState;
  };

  const fetchInternalTalents = (page, nextFilters, nextSortOrder) => {
    dispatch(
      getAssociatedTalentsInit({
        search: searchTerm,
        ...buildInternalFiltersPayload(nextFilters, page, nextSortOrder),
        perPage: PAGE_SIZE,
        pageSize: PAGE_SIZE,
        limit: PAGE_SIZE,
        ...(isAiMode && { aiSearch: true }),
      })
    );
  };

  const applyQueryStateChange = (nextState) => {
    const resolvedState = updateQueryState(nextState);
    fetchInternalTalents(
      resolvedState.currentPage,
      resolvedState.filters,
      resolvedState.sortOrder
    );
    return resolvedState;
  };

  const handleResetFilters = () => {
    try {
      setDraftFilters(INITIAL);
      setSelectedTalentIds([]);
      setFilterOpen(false);
      applyQueryStateChange({ filters: INITIAL, currentPage: 1 });
    } catch (error) {
      console.error("Failed to reset internal talent filters:", error);
    }
  };

  useEffect(() => {
    fetchInternalTalents(currentPage, filters, sortOrder);
  }, []);

  const openFilterDrawer = () => {
    setDraftFilters(filters);
    setFilterOpen(true);
  };

  const handleCloseFilterDrawer = () => {
    setFilterOpen(false);
  };

  const totalCount = rawTotalCount || safeTalents.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const visibleTalentIds =
    safeTalents.length > 0 ? safeTalents.map((talent) => talent.id || talent._id) : [];
  const allSelected =
    visibleTalentIds.length > 0 &&
    visibleTalentIds.every((id) => selectedTalentIds.includes(id));

  const handleSortOrderChange = (nextSortOrder) => {
    setSelectedTalentIds([]);
    applyQueryStateChange({
      sortOrder: nextSortOrder,
      currentPage: 1,
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedTalentIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !visibleTalentIds.includes(id));
      }

      return [...new Set([...prev, ...visibleTalentIds])];
    });
  };

  const handleClearSelectedTalents = () => {
    setSelectedTalentIds([]);
  };

  const handleOpenMatchesModal = () => {
    setMatchesOpen(true);
  };

  const handleCloseMatchesModal = () => {
    setMatchesOpen(false);
  };

  const handleOpenBulkBidModal = () => {
    setBidModal({
      open: true,
      talent: { name: "", title: "", avatar: null, initials: "" },
      talentType: "internal",
      skillListingId: null,
      skillListingIds: selectedTalentIds,
    });
  };

  const handleApplyFilters = (nextFilters) => {
    setSelectedTalentIds([]);
    setFilterOpen(false);
    applyQueryStateChange({
      filters: nextFilters || INITIAL,
      currentPage: 1,
    });
  };

  const handleViewProfile = (id) => {
    history.push(`/internal-detail/${id}`);
  };

  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push(`/query/${encodeURIComponent(searchTerm)}`);
  };

  const handlePlaceBid = (talentItem, talentType) => {
    setBidModal({
      open: true,
      talent: talentItem,
      talentType,
      skillListingId:
        talentItem?.skillListingId ??
        talentItem?.SkillListingId ??
        talentItem?.id ??
        talentItem?._id ??
        null,
    });
  };

  const handleToggleTalentSelect = (id) => {
    setSelectedTalentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectJob = (job) => {
    dispatch(
      addBatchMatches({
        GigId: job.JobOrderId,
        JobTitle: job.title,
        SkillListingIds: selectedTalentIds,
      })
    );
    setMatchesOpen(false);
    setSelectedTalentIds([]);
  };

  const handleCloseBidModal = () => {
    if (bidModal.skillListingIds?.length) {
      setSelectedTalentIds([]);
    }

    setBidModal({
      open: false,
      talent: null,
      talentType: null,
      skillListingId: null,
      skillListingIds: null,
    });
  };

  const handlePageChange = (page) => {
    setSelectedTalentIds([]);
    applyQueryStateChange({ currentPage: page });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderTalentCard = (talent) => {
    const talentId = talent.id || talent._id;

    return (
      <InternalTalentCard
        key={talentId}
        talent={talent}
        profileUrl={`/internal-detail/${talentId}`}
        onViewProfile={handleViewProfile}
        onPlaceBid={handlePlaceBid}
        selectable={!subscriptionFeaturesEnabled}
        selected={selectedTalentIds.includes(talentId)}
        onToggleSelect={handleToggleTalentSelect}
      />
    );
  };

  if (loading) return <Loader />;

  return (
    <div className="qp-container qp-internal-results">
      <QueryPageHeader
        totalMatches={totalCount}
        searchTerm={searchTerm}
        sortOrder={sortOrder}
        onChangeSortOrder={handleSortOrderChange}
        filterCount={getActiveFilterCount(filters)}
        onOpenFilter={openFilterDrawer}
        showSelectAll={!subscriptionFeaturesEnabled && safeTalents.length > 0}
        allSelected={allSelected}
        onToggleSelectAll={handleToggleSelectAll}
        iconEmoji={<InternalTalentIcon />}
        iconClass="qp-icon-internal"
        titleText={`Internal Talent (${totalCount})`}
        onBack={handleBack}
      />

      {selectedTalentIds.length > 0 && (
        <div className="qp-bulk-bar">
          <button className="qp-bulk-btn qp-bulk-btn-cancel" type="button"
            onClick={handleClearSelectedTalents}>
            Cancel
          </button>
          <button className="qp-bulk-btn" type="button" onClick={handleOpenMatchesModal}>
            Add to matches ( {selectedTalentIds.length} Skill listings )
          </button>
          <button className="qp-bulk-btn qp-bulk-btn-primary" type="button"
            onClick={handleOpenBulkBidModal}>
            Place a bid ( {selectedTalentIds.length} Skill listings )
          </button>
        </div>
      )}

      <FilterDrawer
        open={filterOpen}
        values={draftFilters}
        onChange={setDraftFilters}
        hideCompanyPanel
        hideJobTitleAdvancedFields
        onClose={handleCloseFilterDrawer}
        onReset={handleResetFilters}
        onApply={handleApplyFilters}
      />

      <TalentSection
        iconEmoji={<InternalTalentIcon />}
        iconClass="qp-icon-internal"
        titleText="Internal Talent"
        count={totalCount}
        showHeader={false}
      >
        {safeTalents.length === 0 ? (
          <QueryEmptyState onAction={handleResetFilters} searchQuery={searchTerm} talentType="internal" />
        ) : (
          safeTalents.map(renderTalentCard)
        )}
      </TalentSection>

      <SelectJobModal
        open={matchesOpen}
        onClose={handleCloseMatchesModal}
        onSelect={handleSelectJob}
      />

      <PlaceBidModal
        open={bidModal.open}
        talent={bidModal.talent}
        talentType={bidModal.talentType}
        skillListingId={bidModal.skillListingId}
        skillListingIds={bidModal.skillListingIds}
        onClose={handleCloseBidModal}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default InternalTalentResultsPage;
